import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import roomApi, {
  RoomDetail,
  RoomFilters,
  RoomMatch,
  RoomMovie,
} from "../../api/room";
import { ROUTES } from "../../constants/routes";
import { genresData } from "../../constants/structure";
import styles from "./Room.module.scss";

import RoomEntry from "./components/RoomEntry";
import RoomLobby from "./components/RoomLobby";
import RoomHeader from "./components/RoomHeader";
import RoomVideoPlayer from "./components/RoomVideoPlayer";
import RoomMovieCards from "./components/RoomMovieCards";
import RoomMovieInfo from "./components/RoomMovieInfo";
import RoomMatchesSidebar from "./components/RoomMatchesSidebar";
import RoomInviteModal from "./components/RoomInviteModal";
import RoomGenreFilter from "./components/RoomGenreFilter";
import type { Swiper as SwiperType } from "swiper";
import { useDispatch } from "react-redux";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import useLikeClick from "../../hooks/useLikeClick";
import { setLoginModal } from "../../store/slices/modals.slice";
import LinkGuide from "../../components/shared/LinkGuide/LinkGuide";

const STATUS_POLL_MS = 3000;

type Phase = "entry" | "lobby" | "browse" | "results";

const RoomPage: React.FC = () => {
  const { code: urlCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);


  const [phase, setPhase] = useState<Phase>(urlCode ? "lobby" : "entry");
  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [movies, setMovies] = useState<RoomMovie[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [matches, setMatches] = useState<RoomMatch[]>([]);
  const [swiping, setSwiping] = useState(false);
  const [swipedIds, setSwipedIds] = useState<Set<number>>(new Set());
  const [muted, setMuted] = useState(true);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [genreFilterOpen, setGenreFilterOpen] = useState(false);
  const [selectedGenreIds, setSelectedGenreIds] = useState<number[]>([]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const dispatch = useDispatch();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const { likesList, likeHandler } = useLikeClick();
  const openLoginModal = () => dispatch(setLoginModal(true));

  const clearError = () => setError("");

  const getUserId = (): number | null => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split(".")[1])).user_id;
    } catch {
      return null;
    }
  };
  const userId = getUserId();
  const isCreator = room ? room.creator_id === userId : false;

  useEffect(() => {
    if (urlCode) loadRoom(urlCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode]);

  useEffect(() => {
  }, [currentIndex]);

  const applyRoomState = (data: RoomDetail, code: string) => {
    setRoom(data);
    if (data.filters?.genre_ids) {
      setSelectedGenreIds(data.filters.genre_ids);
    }
    if (data.status === "waiting") {
      setPhase("lobby");
    } else if (data.status === "active") {
      setPhase("browse");
      fetchMovies(code);
      fetchMatches(code);
    } else {
      setPhase("results");
      fetchMatches(code);
    }
  };

  const loadRoom = async (code: string) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.getDetail(code);
      applyRoomState(data, code);
    } catch (err: any) {
      if (err.response?.status === 403) {
        try {
          const { data } = await roomApi.join(code);
          applyRoomState(data, code);
          return;
        } catch (joinErr: any) {
          setError(joinErr.response?.data?.detail || "Failed to join room");
        }
      } else {
        setError(err.response?.data?.detail || "Failed to load room");
      }
      setPhase("entry");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (filters: RoomFilters) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.create(filters);
      setRoom(data);
      if (filters.genre_ids) setSelectedGenreIds(filters.genre_ids);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (code: string) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.join(code);
      setRoom(data);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to join room");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (phase === "lobby" && room) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          setRoom((prev) =>
            prev
              ? {
                  ...prev,
                  status: data.status as RoomDetail["status"],
                  participants: data.participants,
                  matches: data.matches,
                }
              : prev
          );
          if (data.status === "active") {
            setPhase("browse");
            fetchMovies(room.code);
          } else if (["completed", "expired", "cancelled"].includes(data.status)) {
            setPhase("results");
          }
        } catch {
          // ignore polling errors
        }
      }, STATUS_POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code]);

  const handleStart = async () => {
    if (!room) return;
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.start(room.code);
      setRoom(data);
      setPhase("browse");
      fetchMovies(room.code);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Cannot start yet");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    if (!room) return;
    try {
      await roomApi.close(room.code);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to close room");
    }
  };

  const fetchMovies = async (code: string) => {
    try {
      const { data } = await roomApi.getMovies(code, 20);
      console.log('daat', data)
      setMovies(data.results.filter((m: RoomMovie) => m.trailer_key !== null));
      setHasMore(data.has_more);
      setCurrentIndex(0);
    } catch {
      // ignore
    }
  };

  const fetchMatches = async (code: string) => {
    try {
      const { data } = await roomApi.getMatches(code);
      setMatches([...data].sort((a, b) => a.id - b.id));
    } catch {
      // ignore
    }
  };

  const fetchMoreMovies = useCallback(async () => {
    if (!room || !hasMore) return;
    try {
      const offset = movies.length;
      const { data } = await roomApi.getMoviesAtOffset(room.code, offset, 20);
      setMovies((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newMovies = data.results.filter((m: RoomMovie) => m.trailer_key !== null && !existingIds.has(m.id));
        return [...prev, ...newMovies];
      });
      setHasMore(data.has_more);
    } catch {
      // ignore
    }
  }, [room, hasMore, movies.length]);

  const handleBrokenTrailer = useCallback(() => {
    const movie = movies[currentIndex];
    if (!movie) return;
    const brokenId = movie.id;
    setMovies((prev) => prev.filter((m) => m.id !== brokenId));
  }, [movies, currentIndex]);

  const handleNavigateUp = useCallback(() => {
    if (currentIndex > 0) {
      //setCurrentIndex((i) => i - 1);
      //swiperRef.current?.slidePrev();
    }
  }, [currentIndex]);

  const handleNavigateDown = useCallback(() => {
    if (currentIndex < movies.length - 1) {
      //setCurrentIndex((i) => i + 1);
      //swiperRef.current?.slideNext();
      if (currentIndex >= movies.length - 5 && hasMore) {
        fetchMoreMovies();
      }
    }
  }, [currentIndex, movies.length, hasMore, fetchMoreMovies]);

  const handleLike = useCallback(async () => {
    const movie = movies[currentIndex];
    if (!room || swiping || !movie || swipedIds.has(movie.id)) return;

    setSwiping(true);

    try {
      const { data } = await roomApi.swipe(room.code, movie.id, "like");

      setSwipedIds((prev) => new Set(prev).add(movie.id));

      setMovies((prev) =>
        prev.map((m) =>
          m.id === movie.id ? { ...m, likes_count: m.likes_count + 1 } : m
        )
      );

      if (data.match) {
        fetchMatches(room.code);
        if (matches.length === 0) setSidebarOpen(true);
      }

      if (currentIndex < movies.length - 1) {
        swiperRef.current?.slideNext();

        if (currentIndex >= movies.length - 5 && hasMore) {
          fetchMoreMovies();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSwiping(false);
    }
  }, [
    room,
    swiping,
    movies,
    currentIndex,
    swipedIds,
    matches.length,
    hasMore,
    fetchMoreMovies,
  ]);

  const handleDislike = useCallback(async () => {
    const movie = movies[currentIndex];
    if (!room || swiping || !movie || swipedIds.has(movie.id)) return;

    setSwiping(true);

    try {
      await roomApi.swipe(room.code, movie.id, "dislike");

      setSwipedIds((prev) => new Set(prev).add(movie.id));

      if (currentIndex < movies.length - 1) {
        swiperRef.current?.slideNext();

        if (currentIndex >= movies.length - 5 && hasMore) {
          fetchMoreMovies();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSwiping(false);
    }
  }, [
    room,
    swiping,
    movies,
    currentIndex,
    swipedIds,
    hasMore,
    fetchMoreMovies,
  ]);

  useEffect(() => {
    if (phase !== "browse") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp") handleNavigateUp();
      if (e.key === "ArrowDown") handleNavigateDown();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, handleNavigateUp, handleNavigateDown]);

  useEffect(() => {
    if (phase === "browse" && room) {
      const interval = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          setRoom((prev) =>
            prev
              ? {
                  ...prev,
                  status: data.status as RoomDetail["status"],
                  participants: data.participants,
                  matches: data.matches,
                }
              : prev
          );
          if (["completed", "expired", "cancelled"].includes(data.status)) {
            setPhase("results");
          }
          setMatches((prev) => {
            if (data.matches.length > prev.length) {
              if (prev.length === 0) setSidebarOpen(true);
              return [...data.matches].sort((a, b) => a.id - b.id);
            }
            return prev;
          });
        } catch {
          // ignore
        }
      }, STATUS_POLL_MS);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code]);

  const handleSelectMatch = async (matchId: number) => {
    if (!room) return;
    try {
      await roomApi.selectMatch(room.code, matchId);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to select match");
    }
  };

  const handleNewRoom = () => {
    setRoom(null);
    setPhase("entry");
    setMovies([]);
    setMatches([]);
    setCurrentIndex(0);
    setSwipedIds(new Set());
    setSidebarOpen(false);
    clearError();
    navigate(ROUTES.ROOM, { replace: true });
  };

  const toggleGenre = (id: number) => {
    setSelectedGenreIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const getActiveGenreNames = (): string[] => {
    return selectedGenreIds
      .map((id) => genresData.find((g) => g.id === id)?.name)
      .filter(Boolean) as string[];
  };

  const currentMovie = movies[currentIndex] || null;
  const selectedMatch = room?.matches?.find((m) => m.is_selected);
  const isCurrentMovieSwiped = currentMovie
    ? swipedIds.has(currentMovie.id)
    : false;

  return (
    <>
      <Header className={styles.headerWrapper} />
      <div  className={`${styles.page} ${ phase === "browse" ? styles.pageBrowse : "" }`}>
        {phase === "entry" && (
          <RoomEntry
            onCreateRoom={handleCreate}
            onJoinRoom={handleJoin}
            loading={loading}
            error={error}
          />
        )}

        {phase === "lobby" && room && (
          <RoomLobby
            room={room}
            isCreator={isCreator}
            onStart={handleStart}
            onClose={handleClose}
            loading={loading}
            error={error}
          />
        )}

        {phase === "browse" && room && (
          <div className={styles.browseLayout}>
            <RoomMatchesSidebar
              matches={matches}
              isOpen={true}
              isCreator={isCreator}
              onClose={() => setSidebarOpen(false)}
              onSelectMatch={handleSelectMatch}
            />

            <div className={styles.mainContent}>
              <RoomHeader
                activeGenreNames={getActiveGenreNames()}
                filterCount={selectedGenreIds.length}
                participants={room.participants}
                matchCount={matches.length}
                onFilterClick={() => setGenreFilterOpen(true)}
                onMatchesClick={() => setSidebarOpen(!sidebarOpen)}
                onInviteClick={() => setInviteModalOpen(true)}
                isCreator={isCreator}
                onEndSession={handleClose}
              />

              {currentMovie ? (
                <div className={styles.playerSection}>
                  <div className={styles.playerRow}>
                    <RoomVideoPlayer
                      trailerKey={currentMovie.trailer_key}
                      backdropPath={currentMovie.backdrop_path}
                      muted={muted}
                      onToggleMuted={() => setMuted(!muted)}
                      onTrailerError={handleBrokenTrailer}
                      navigation={{
                        mode: "actions",
                        movie: currentMovie,
                        onLike: handleLike,
                        onDislike: handleDislike,
                        isLiked: isCurrentMovieSwiped,
                        isSwiping: swiping,
                      }}
                    />
                    <RoomMovieCards
                      movies={movies}
                      activeIndex={currentIndex}
                      onMovieClick={setCurrentIndex}
                      onSlideChange={setCurrentIndex}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                    />
                  </div>

                  <RoomMovieInfo
                    movie={currentMovie}
                    onLike={handleLike}
                    onDislike={handleDislike}
                    onShare={() => setInviteModalOpen(true)}
                    isLiked={isCurrentMovieSwiped}
                    isSwiping={swiping}
                    favouritesHandler={favouritesHandler}
                    isFavorited={(favoritesList?.data?.results || []).includes(currentMovie.id)}
                    openLoginModal={openLoginModal}
                    likesList={likesList?.data?.results || []}
                    likeHandler={likeHandler}
                  />
                </div>
              ) : (
                <div className={styles.emptyState}>
                  {t("room_no_more_movies", "No more movies")}
                </div>
              )}

            </div>


          </div>
        )}

        {phase === "results" && room && (
          <div className={styles.results}>
            <div className={styles.resultsHeading}>
              <div className={styles.resultsTitle}>
                {room.status === "completed"
                  ? t("room_completed", "Movie Selected!")
                  : room.status === "expired"
                    ? t("room_expired", "Room Expired")
                    : t("room_cancelled", "Room Cancelled")}
              </div>
              <span className={styles.resultsSubTitle}>{t("room_new_room_subtitle", "Create a new room")}</span>
            </div>


            {selectedMatch && (
              <div className={styles.resultCard}>
                {selectedMatch.movie_poster_path && (
                  <img
                    src={`${process.env.REACT_APP_IMG_BASE_URL}${selectedMatch.movie_poster_path}`}
                    alt={selectedMatch.movie_title}
                    className={styles.resultPoster}
                  />
                )}
                <div className={styles.resultBox}>
                  <div className={styles.resultTitle}>
                    {selectedMatch.movie_title}
                  </div>
                  <div className={styles.resultSubtitle}>
                    {t("room_you_picked", "You all picked this one!")}
                  </div>
                </div>

              </div>
            )}

            {matches.length > 0 && (
              <div className={styles.allMatches}>
                <div className={styles.allMatchesTitle}>
                  {t("room_all_matches", "All Matches")} ({matches.length})
                </div>
                {matches.map((m) => (
                  <div key={m.id} className={styles.matchItem}>
                    {m.movie_poster_path && (
                      <img
                        src={`${process.env.REACT_APP_IMG_BASE_URL}${m.movie_poster_path}`}
                        alt={m.movie_title}
                        className={styles.matchItemPoster}
                      />
                    )}
                    <span className={styles.matchItemTitle}>
                      {m.movie_title}
                    </span>
                    {m.is_selected && (
                      <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>
                        {t("room_selected", "Selected")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button className={styles.newRoomBtn} onClick={handleNewRoom}>
              {t("room_new_room", "Create New Room")}
            </button>
          </div>
        )}
      </div>
      <Footer source="" />
      {phase === "browse" && room && (
        <>
          <RoomInviteModal
            roomCode={room.code}
            isOpen={inviteModalOpen}
            onClose={() => setInviteModalOpen(false)}
          />
          <RoomGenreFilter
            selectedGenreIds={selectedGenreIds}
            onToggleGenre={toggleGenre}
            onApply={() => setGenreFilterOpen(false)}
            onClose={() => setGenreFilterOpen(false)}
            isOpen={genreFilterOpen}
          />
        </>

      )}
      <LinkGuide/>
    </>
  );
};

export default RoomPage;
